import React from 'react'
import { Box, Typography, Chip, Tooltip } from '@mui/material'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { UrlMapping, ValidationError } from '../types'

interface UrlMappingTableProps {
  data: UrlMapping[]
  validationErrors?: ValidationError[]
}

export const UrlMappingTable: React.FC<UrlMappingTableProps> = ({
  data,
  validationErrors = [],
}) => {
  // Create a map of row numbers to error messages for quick lookup
  const errorMap = validationErrors.reduce(
    (acc, error) => {
      const dataRowIndex = error.row - 2 // Convert from 1-indexed spreadsheet row to 0-indexed data array
      if (!acc[dataRowIndex]) {
        acc[dataRowIndex] = []
      }
      acc[dataRowIndex].push(error.message)
      return acc
    },
    {} as Record<number, string[]>,
  )

  const columns: Column<UrlMapping>[] = [
    {
      title: 'Old URL',
      width: '45%',
      rowContent: (item: UrlMapping) => (
        <Tooltip title={item.old_url}>
          <Typography variant="body2" noWrap>
            {item.old_url}
          </Typography>
        </Tooltip>
      ),
      rowString: (item: UrlMapping) => item.old_url,
    },
    {
      title: 'New URL',
      width: '45%',
      rowContent: (item: UrlMapping) => (
        <Tooltip title={item.new_url}>
          <Typography variant="body2" noWrap>
            {item.new_url}
          </Typography>
        </Tooltip>
      ),
      rowString: (item: UrlMapping) => item.new_url,
    },
  ]

  // Function to style rows with errors
  const getRowStyle = (index: number) => {
    if (errorMap[index]) {
      return {
        bgcolor: '#fff3e0', // Light orange background for error rows
      }
    }
    return undefined
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">
          URL Mappings
          <Chip
            label={`${data.length} mapping${data.length !== 1 ? 's' : ''}`}
            size="small"
            sx={{ ml: 2 }}
          />
        </Typography>
        {validationErrors.length > 0 && (
          <Chip
            label={`${validationErrors.length} validation issue${validationErrors.length !== 1 ? 's' : ''}`}
            color="warning"
            size="small"
          />
        )}
      </Box>

      {validationErrors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="warning.main" gutterBottom>
            Validation Issues:
          </Typography>
          {validationErrors.slice(0, 5).map((error, idx) => (
            <Typography
              key={idx}
              variant="body2"
              color="text.secondary"
              sx={{ ml: 2 }}
            >
              • Row {error.row}: {error.message}
            </Typography>
          ))}
          {validationErrors.length > 5 && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              ... and {validationErrors.length - 5} more
            </Typography>
          )}
        </Box>
      )}

      <Table
        columns={columns}
        items={data}
        overrideHeight={500}
        canExport={true}
        getRowStyle={getRowStyle}
      />
    </Box>
  )
}
